import { FC, FocusEvent, MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Box, Button, Flex, Input, Spinner, Tag, TagCloseButton, TagLabel, Text, useToast, VStack } from '@bonusly/design-system';

import { useDebounceValue } from '../../hooks/use-debounce-value';
import { useGithubSearchRepos, useGithubUpdatePreferences } from '../../queries/github';
import { GithubRepo } from '../../schemas/github';

interface GithubScopePickerProps {
	selectedOrgs: string[];
	selectedRepos: string[];
}

export const GithubScopePicker: FC<GithubScopePickerProps> = ({ selectedOrgs: initialOrgs, selectedRepos: initialRepos }) => {
	const [selectedOrgs, setSelectedOrgs] = useState<string[]>(initialOrgs);
	const [selectedRepos, setSelectedRepos] = useState<string[]>(initialRepos);
	const [searchQuery, setSearchQuery] = useState('');
	const [isOpen, setIsOpen] = useState(false);
	const debouncedQuery = useDebounceValue(searchQuery, 300);
	const toast = useToast();
	const inputRef = useRef<HTMLInputElement>(null);
	const popoverRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		setSelectedOrgs(initialOrgs);
	}, [initialOrgs]);

	useEffect(() => {
		setSelectedRepos(initialRepos);
	}, [initialRepos]);

	const { data: searchResults, isLoading: isSearching } = useGithubSearchRepos(debouncedQuery);
	const { mutateAsync: updatePreferences, isLoading: isSaving } = useGithubUpdatePreferences();

	const isDirty = useMemo(() => {
		if (initialOrgs.length !== selectedOrgs.length || initialRepos.length !== selectedRepos.length) return true;
		return !initialOrgs.every((o) => selectedOrgs.includes(o)) || !initialRepos.every((r) => selectedRepos.includes(r));
	}, [initialOrgs, initialRepos, selectedOrgs, selectedRepos]);

	const addRepo = useCallback(
		(repo: GithubRepo) => {
			if (!selectedRepos.includes(repo.fullName)) {
				setSelectedRepos((prev) => [...prev, repo.fullName]);
			}
			setSearchQuery('');
			inputRef.current?.focus();
		},
		[selectedRepos]
	);

	const addOrg = useCallback(
		(owner: string) => {
			if (!selectedOrgs.includes(owner)) {
				setSelectedOrgs((prev) => [...prev, owner]);
			}
			setSearchQuery('');
			inputRef.current?.focus();
		},
		[selectedOrgs]
	);

	const removeRepo = useCallback((fullName: string) => {
		setSelectedRepos((prev) => prev.filter((r) => r !== fullName));
	}, []);

	const removeOrg = useCallback((login: string) => {
		setSelectedOrgs((prev) => prev.filter((o) => o !== login));
	}, []);

	const handleSave = useCallback(async () => {
		try {
			await updatePreferences({
				selected_orgs: selectedOrgs,
				selected_repos: selectedRepos,
			});
		} catch {
			toast({ title: 'Failed to save preferences', status: 'error', duration: 3000 });
		}
	}, [selectedOrgs, selectedRepos, updatePreferences, toast]);

	const hasSelections = selectedOrgs.length > 0 || selectedRepos.length > 0;
	const isQueryInSync = searchQuery === debouncedQuery;
	const isDebouncedSearchActive = debouncedQuery.length >= 2;
	const canShowSearchResults = isQueryInSync && isDebouncedSearchActive;

	const filteredResults = useMemo(() => {
		if (!canShowSearchResults || !searchResults?.repos) return [];
		return searchResults.repos.filter((r) => !selectedRepos.includes(r.fullName) && !selectedOrgs.includes(r.owner));
	}, [canShowSearchResults, searchResults, selectedRepos, selectedOrgs]);

	const resultOrgs = useMemo(() => {
		const q = debouncedQuery.toLowerCase();
		const orgOwners = new Set(filteredResults.filter((r) => r.ownerType === 'Organization').map((r) => r.owner));
		return Array.from(orgOwners).filter((o) => !selectedOrgs.includes(o) && o.toLowerCase().includes(q));
	}, [filteredResults, selectedOrgs, debouncedQuery]);

	const hasDropdownResults = resultOrgs.length > 0 || filteredResults.length > 0;
	const showDropdown = isOpen && searchQuery.length > 0;
	const isSearchPending = searchQuery.length >= 2 && (isSearching || !isQueryInSync);

	const handleInputBlur = (event: FocusEvent<HTMLInputElement>) => {
		const next = event.relatedTarget as Node | null;
		if (next && popoverRef.current?.contains(next)) return;
		setIsOpen(false);
	};

	const handleFieldMouseDown = (event: MouseEvent<HTMLDivElement>) => {
		const target = event.target as HTMLElement;
		if (target === inputRef.current) return;
		if (target.closest('button')) return;
		event.preventDefault();
		inputRef.current?.focus();
	};

	return (
		<Box bg="bg.canvas" borderRadius="lg" mt={3} p={4}>
			<Text size="md" variant="strong" color="text.default" mb={1}>
				Shared with Bizy
			</Text>
			<Text size="sm" color="text.softer" mb={3}>
				{hasSelections ? 'Bizy can only see repos and orgs you select.' : 'Nothing shared yet — Bizy cannot see any repos.'}
			</Text>

			<Box position="relative">
				<Flex
					align="center"
					bg="bg.surface"
					border="1px solid"
					borderColor={isOpen ? 'border.focus' : 'border.default'}
					borderRadius="md"
					cursor="text"
					flexWrap="wrap"
					gap={1.5}
					minH="10"
					px={2}
					py={1.5}
					onMouseDown={handleFieldMouseDown}
				>
					{selectedOrgs.map((org) => (
						<Tag key={`org:${org}`} size="sm" colorScheme="blue" borderRadius="full" maxW="full">
							<TagLabel wordBreak="break-all">org:{org}</TagLabel>
							<TagCloseButton onClick={() => removeOrg(org)} />
						</Tag>
					))}
					{selectedRepos.map((repo) => (
						<Tag key={`repo:${repo}`} size="sm" borderRadius="full" maxW="full">
							<TagLabel wordBreak="break-all">repo:{repo}</TagLabel>
							<TagCloseButton onClick={() => removeRepo(repo)} />
						</Tag>
					))}
					<Flex align="center" flex={1} gap={2} minW="8rem">
						<Input
							ref={inputRef}
							aria-expanded={isOpen}
							flex={1}
							minW={0}
							onBlur={handleInputBlur}
							onChange={(event) => setSearchQuery(event.target.value)}
							onFocus={() => setIsOpen(true)}
							placeholder="Search repos..."
							role="combobox"
							size="sm"
							value={searchQuery}
							variant="unstyled"
							_placeholder={{ color: 'text.softest' }}
						/>
						{isSearchPending && <Spinner flexShrink={0} size="xs" />}
					</Flex>
				</Flex>

				{showDropdown && (
					<Box
						ref={popoverRef}
						bg="bg.surface"
						border="1px solid"
						borderColor="border.soft"
						borderRadius="md"
						boxShadow="sm"
						left={0}
						maxH="160px"
						mt={1}
						overflowY="auto"
						position="absolute"
						right={0}
						top="100%"
						zIndex={1}
						onMouseDown={(event) => event.preventDefault()}
					>
						{searchQuery.length > 0 && searchQuery.length < 2 && (
							<Box px={3} py={2}>
								<Text color="text.softer" size="sm">
									Type at least 2 characters to search
								</Text>
							</Box>
						)}

						{canShowSearchResults && !isSearching && !hasDropdownResults && (
							<Box px={3} py={2}>
								<Text color="text.softer" size="sm">
									No repos found
								</Text>
							</Box>
						)}

						{canShowSearchResults && hasDropdownResults && (
							<VStack align="stretch" spacing={0}>
								{resultOrgs.map((owner) => (
									<Flex
										key={`org-add:${owner}`}
										alignItems="center"
										cursor="pointer"
										px={3}
										py={1.5}
										_hover={{ bg: 'secondary.bgAlpha' }}
										onClick={() => addOrg(owner)}
									>
										<Text size="sm" variant="strong" wordBreak="break-all">
											org:{owner} (entire org)
										</Text>
									</Flex>
								))}
								{filteredResults.map((repo) => (
									<Flex
										key={repo.fullName}
										alignItems="center"
										cursor="pointer"
										px={3}
										py={1.5}
										_hover={{ bg: 'secondary.bgAlpha' }}
										onClick={() => addRepo(repo)}
									>
										<Text size="sm" wordBreak="break-all">
											repo:{repo.fullName}
										</Text>
										{repo.private && (
											<Text color="text.softer" flexShrink={0} ml={1} size="sm">
												(private)
											</Text>
										)}
									</Flex>
								))}
							</VStack>
						)}
					</Box>
				)}
			</Box>

			{isDirty && (
				<Button colorScheme="primary" isLoading={isSaving} mt={3} onClick={handleSave} size="md">
					Save
				</Button>
			)}
		</Box>
	);
};
